import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Divider,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles(theme => ({
	// previa fiel ao cartao nativo do WhatsApp
	waCard: {
		borderRadius: 12,
		overflow: "hidden",
		backgroundColor:
			theme.palette.type === "dark" ? "#005c4b" : "#d9fdd3",
		color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
	},
	waCardTop: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "12px 14px",
	},
	waCardIcon: {
		width: 34,
		height: 34,
		borderRadius: 8,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#00a884",
		color: "#fff",
		flexShrink: 0,
	},
	waCardName: {
		fontWeight: 600,
		fontSize: "0.88rem",
		lineHeight: 1.3,
	},
	waCardKey: {
		fontSize: "0.78rem",
		opacity: 0.8,
		wordBreak: "break-all",
	},
	waCardDivider: {
		backgroundColor: "rgba(134, 150, 160, 0.3)",
	},
	waCardButton: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		padding: "10px 0",
		color: "#00a884",
		fontWeight: 600,
		fontSize: "0.85rem",
		cursor: "pointer",
		userSelect: "none",
		"&:hover": {
			backgroundColor: "rgba(0, 168, 132, 0.08)",
		},
	},
	hint: {
		marginTop: 12,
		fontSize: "0.78rem",
		color: theme.custom.textSecondary,
		textAlign: "center",
	},
	emptyBox: {
		textAlign: "center",
		padding: "24px 12px",
	},
	emptyIcon: {
		fontSize: 40,
		color: theme.custom.textSecondary,
		marginBottom: 8,
	},
}));

const KEY_TYPE_LABELS = () => ({
	cpf: "CPF",
	cnpj: "CNPJ",
	phone: i18n.t("pix.keyTypes.phone"),
	email: "E-mail",
	random: i18n.t("pix.keyTypes.random"),
});

const PixDialog = ({ open, onClose, ticketId }) => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const [config, setConfig] = useState(null);
	const [loadingConfig, setLoadingConfig] = useState(true);
	const [sending, setSending] = useState(false);

	useEffect(() => {
		if (!open) return;
		setLoadingConfig(true);
		const fetchConfig = async () => {
			try {
				const { data } = await api.get("/pix-config");
				setConfig(data);
			} catch (err) {
				toastError(err);
			}
			setLoadingConfig(false);
		};
		fetchConfig();
	}, [open]);

	const isConfigured = config?.pixKey && config?.pixName;
	const keyLabel = isConfigured
		? KEY_TYPE_LABELS()[config.pixKeyType] || "Pix"
		: "";

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(config.pixKey);
			toast.success(i18n.t("pix.dialog.copied"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSend = async () => {
		if (!ticketId || !isConfigured) return;
		setSending(true);
		try {
			await api.post(`/messages/${ticketId}/pix`);
			toast.success(i18n.t("pix.dialog.sent"));
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSending(false);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle>{i18n.t("pix.dialog.title")}</DialogTitle>
			<DialogContent dividers>
				{loadingConfig ? null : !isConfigured ? (
					<div className={classes.emptyBox}>
						<AttachMoneyIcon className={classes.emptyIcon} />
						<Typography variant="body1" gutterBottom>
							{i18n.t("pix.dialog.notConfiguredTitle")}
						</Typography>
						<Typography variant="body2" color="textSecondary">
							{user?.profile === "admin"
								? i18n.t("pix.dialog.notConfiguredAdmin")
								: i18n.t("pix.dialog.notConfiguredUser")}
						</Typography>
						{user?.profile === "admin" && (
							<Button
								style={{ marginTop: 16 }}
								variant="outlined"
								color="primary"
								onClick={() => history.push("/settings")}
							>
								{i18n.t("pix.dialog.goToSettings")}
							</Button>
						)}
					</div>
				) : (
					<>
						<div className={classes.waCard}>
							<div className={classes.waCardTop}>
								<div className={classes.waCardIcon}>
									<AttachMoneyIcon fontSize="small" />
								</div>
								<div>
									<Typography className={classes.waCardName}>
										{config.pixName}
									</Typography>
									<Typography className={classes.waCardKey}>
										{keyLabel}: {config.pixKey}
									</Typography>
								</div>
							</div>
							<Divider className={classes.waCardDivider} />
							<div
								className={classes.waCardButton}
								onClick={handleCopy}
								role="button"
							>
								<FileCopyOutlinedIcon fontSize="small" />
								{i18n.t("pix.dialog.copyKey")}
							</div>
						</div>
						<Typography className={classes.hint}>
							{i18n.t("pix.dialog.hint")}
						</Typography>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary" disabled={sending}>
					{i18n.t("pix.dialog.cancel")}
				</Button>
				{isConfigured && ticketId && (
					<ButtonWithSpinner
						loading={sending}
						onClick={handleSend}
						color="primary"
						variant="contained"
					>
						{i18n.t("pix.dialog.send")}
					</ButtonWithSpinner>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default PixDialog;
